const DIRECT_COMMANDS = new Set([
  'cancel',
  'enough',
  'hold',
  'pause',
  'sonny',
  'stop',
  'wait',
]);

// These words must remain actionable even when Sonny happened to say the
// same word. The spoken name is intentionally excluded: speech recognition
// renders Sonny and Sunny interchangeably, so treating the name as immune to
// echo detection lets Sonny's own audio terminate its playback.
const ECHO_IMMUNE_COMMANDS = new Set([
  'cancel',
  'enough',
  'hold',
  'pause',
  'stop',
  'wait',
]);

const CONVERSATIONAL_CUES = [
  'actually',
  'before you',
  'can you',
  'could you',
  'excuse me',
  'hold on',
  'i mean',
  'i disagree',
  'i have',
  'let me',
  'not quite',
  'no wait',
  'one second',
  'sonny',
  'sounds good',
  "that isn't",
  "that's not",
  "that's wrong",
  'you missed',
  'wait a second',
  'what about',
  'one works',
  'option works',
  'works better',
];

const SINGLE_WORD_TURN_CUES = new Set([
  'actually',
  'different',
  'no',
  'sonny',
  'wait',
  'why',
  'yes',
]);

const TURN_OPENERS = new Set([
  'actually',
  'but',
  'can',
  'could',
  'check',
  'do',
  'how',
  'i',
  "i'd",
  "i'm",
  "i've",
  'let',
  'my',
  'no',
  'please',
  'explain',
  'show',
  'so',
  'sonny',
  'tell',
  'that',
  'this',
  'wait',
  'we',
  'what',
  'when',
  'where',
  'which',
  'who',
  'why',
  'would',
  'yes',
  'you',
  'your',
]);

const SHORT_REQUEST_OPENERS = new Set(['check', 'explain', 'show', 'tell']);

const NON_SPEECH_FRAGMENTS = new Set([
  'ah',
  'hmm',
  'mhm',
  'mm',
  'noise',
  'uh',
  'um',
]);

// Floor-control commands stop the current output but are not themselves chat
// content. Sending "wait a second" to the model lets stale history turn a
// simple handoff into an unrelated response. Keep this set deliberately
// narrow: once substantive words are added, the full utterance is a new turn.
const CONTROL_ONLY_COMMANDS = new Set([
  'cancel',
  'cancel please',
  'enough',
  'hold',
  'hold on',
  'hold on a moment',
  'hold on a second',
  'hold on a minute',
  'pause',
  'pause please',
  'sonny',
  'stop',
  'stop please',
  'wait',
  'wait please',
  'wait a moment',
  'wait a second',
  'wait a minute',
]);

const RECORDED_ACTIVITY_MIN_RMS = 0.018;
const RECORDED_ACTIVITY_MAX_THRESHOLD = 0.09;

export function rearmBargeInRecovery({
  currentTimer,
  clearTimer,
  scheduleTimer,
  onRecover,
  recoveryMs,
  minimumMs = 360,
}) {
  if (currentTimer !== null && currentTimer !== undefined) clearTimer(currentTimer);
  const numericDelay = Number(recoveryMs);
  const delay = Math.max(
    minimumMs,
    Number.isFinite(numericDelay) && numericDelay > 0 ? numericDelay : minimumMs,
  );
  return scheduleTimer(onRecover, delay);
}

export function normalizedVoiceWords(value) {
  return String(value || '')
    .normalize('NFKC')
    .toLocaleLowerCase('en-US')
    .replace(/[^\p{L}\p{N}'\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
    // Speech recognizers commonly produce the homophone “Sunny.” Treat it as
    // Sonny for echo and turn-intent decisions without rewriting the caption
    // the person sees or correcting them conversationally.
    .map((word) => (word === 'sunny' ? 'sonny' : word));
}

// Reject recognizer failure loops without treating ordinary repetition as
// invalid speech. Mobile transcription services can occasionally turn a
// notification tone or a corrupt audio tail into hundreds of copies of one
// token (for example, "5,5,5,5..."). Such output is not a credible user turn
// and must never reach persistence or the model.
export function isPathologicalVoiceTranscript(value) {
  const words = normalizedVoiceWords(value);
  if (words.length < 4) return false;

  const counts = new Map();
  let longestRun = 0;
  let currentRun = 0;
  let previous = null;
  for (const word of words) {
    counts.set(word, (counts.get(word) || 0) + 1);
    if (word === previous) currentRun += 1;
    else currentRun = 1;
    previous = word;
    longestRun = Math.max(longestRun, currentRun);
  }
  const dominantCount = Math.max(...counts.values());
  const repeatedFloorCommand = counts.size === 1 && ECHO_IMMUNE_COMMANDS.has(words[0]);
  if (repeatedFloorCommand) return false;
  // Chrome can surface a recognition feedback loop progressively ("hello",
  // "hello hello", ...). Reject a four-token uninterrupted loop immediately
  // instead of waiting for eight copies to reach the caption. Mixed ordinary
  // repetition still needs the longer, dominant-token boundary below.
  return longestRun >= 4 || (words.length >= 6 && dominantCount / words.length >= 0.72);
}

// Rebuild the current recognition transcript from the browser's complete
// result list. Browsers may resend an already-finalized segment, revise an
// interim tail, or move resultIndex backwards. Snapshotting by result slot
// avoids duplicate words and preserves every settled segment.
export function recognitionResultSnapshot(event) {
  const results = event?.results;
  if (!results?.length) {
    return {
      candidate: '', finalCandidate: '', interimCandidate: '',
      confidence: 0, hasFinal: false, hasInterim: false,
    };
  }
  const finalParts = [];
  const interimParts = [];
  let confidence = 0;
  for (let index = 0; index < results.length; index += 1) {
    const result = results[index];
    const alternative = result?.[0];
    const text = String(alternative?.transcript || '').trim();
    if (!text) continue;
    confidence = alternative?.confidence;
    const parts = result?.isFinal === true ? finalParts : interimParts;
    if (parts.at(-1) !== text) parts.push(text);
  }
  const finalCandidate = finalParts.join(' ').replace(/\s+/g, ' ').trim();
  const interimCandidate = interimParts.join(' ').replace(/\s+/g, ' ').trim();
  return {
    candidate: [finalCandidate, interimCandidate].filter(Boolean).join(' '),
    finalCandidate,
    interimCandidate,
    confidence,
    hasFinal: finalParts.length > 0,
    hasInterim: interimParts.length > 0,
  };
}

export function isControlOnlyVoiceCommand(value) {
  const words = normalizedVoiceWords(value);
  const phrase = words.join(' ');
  if (CONTROL_ONLY_COMMANDS.has(phrase)) return true;

  // Web Speech occasionally emits the same finalized result in two adjacent
  // slots (for example, "wait a second wait a second"). Treat an exact
  // repetition of a narrow floor-control command as that command, never as a
  // model prompt. Substantive repeated language remains untouched.
  for (let partLength = 1; partLength <= Math.floor(words.length / 2); partLength += 1) {
    if (words.length % partLength !== 0) continue;
    const part = words.slice(0, partLength);
    if (!CONTROL_ONLY_COMMANDS.has(part.join(' '))) continue;
    const repeated = words.every((word, index) => word === part[index % partLength]);
    if (repeated) return true;
  }
  return false;
}

export function isLikelyAssistantEcho(candidate, assistantText) {
  const heard = normalizedVoiceWords(candidate);
  const spoken = normalizedVoiceWords(assistantText);
  if (!heard.length || !spoken.length) return false;
  if (heard.length === 1) {
    return !ECHO_IMMUNE_COMMANDS.has(heard[0]) && spoken.includes(heard[0]);
  }

  const assistantWords = new Set(spoken);
  const shared = heard.filter((word) => assistantWords.has(word)).length;
  const overlap = shared / heard.length;
  const phrase = heard.join(' ');
  return overlap >= 0.72 || spoken.join(' ').includes(phrase);
}

export function isExplicitReplacementTurn(candidate, assistantText = '') {
  const words = normalizedVoiceWords(candidate);
  if (words.length < 3) return false;
  const openings = new Set(['actually', 'but', 'no', 'wait']);
  // Chrome may prepend words leaking from active playback to a genuine user
  // interruption. Evaluate a bounded correction suffix instead of requiring
  // the cue to be the first recognized word, but never exempt a suffix that
  // is itself present in Sonny's spoken response from echo rejection.
  for (let index = words.length - 3; index >= 0; index -= 1) {
    if (!openings.has(words[index])) continue;
    const suffixWords = words.slice(index);
    const suffix = suffixWords.join(' ');
    const hasReplacementMeaning = suffixWords.includes('instead') ||
      suffix.includes('not what i') ||
      suffix.includes("that's wrong") ||
      suffix.includes('that is wrong');
    if (!hasReplacementMeaning) continue;
    const spoken = normalizedVoiceWords(assistantText).join(' ');
    if (spoken && spoken.includes(suffix)) continue;
    return true;
  }
  return false;
}

export function shouldIgnoreRecentAssistantEcho({
  candidate,
  assistantText,
  elapsedMs,
  cooldownMs = 2400,
}) {
  const elapsed = Number(elapsedMs);
  if (!Number.isFinite(elapsed) || elapsed < 0 || elapsed > cooldownMs) return false;
  return isLikelyAssistantEcho(candidate, assistantText);
}

// Firefox does not currently expose the Web Speech recognition API. Its
// fallback monitor therefore uses local microphone energy only to decide when
// to pause playback and collect a short, bounded audio fragment for the same
// private transcription endpoint used by ordinary live turns. This helper is
// deliberately pure: it never sees, stores, or labels a person's audio.
export function evaluateRecordedVoiceActivity({
  rms,
  peak,
  noiseFloor = 0.008,
  consecutiveActiveFrames = 0,
}) {
  const safeRms = Number.isFinite(Number(rms)) ? Math.max(0, Number(rms)) : 0;
  const safeNoiseFloor = Number.isFinite(Number(noiseFloor))
    ? Math.max(0, Number(noiseFloor))
    : 0.008;
  const threshold = Math.min(
    RECORDED_ACTIVITY_MAX_THRESHOLD,
    Math.max(RECORDED_ACTIVITY_MIN_RMS, safeNoiseFloor * 2.4 + 0.006),
  );
  const safePeak = Number.isFinite(Number(peak)) ? Math.max(0, Number(peak)) : null;
  const crestFactor = safePeak === null || safeRms <= 0 ? null : safePeak / safeRms;
  // A dropped object or click concentrates nearly all energy in one sample.
  // Reject that frame when peak data is available; ordinary speech remains
  // eligible and still must persist across three frames before yielding.
  const impulseLike = crestFactor !== null && crestFactor >= 9;
  const active = safeRms >= threshold && !impulseLike;
  const nextConsecutiveActiveFrames = active
    ? Math.max(0, Number(consecutiveActiveFrames) || 0) + 1
    : 0;

  return {
    active,
    // A single impulse can be a click, dropped object, or speaker transient.
    // Require three 40 ms samples before the raw-energy fallback yields. Native
    // speechstart/interim recognition still pauses a real spoken word sooner.
    confirmed: active && nextConsecutiveActiveFrames >= 3,
    nextConsecutiveActiveFrames,
    threshold,
    impulseLike,
  };
}

export function classifyVoiceInterruption({
  candidate,
  assistantText,
  confidence,
  isFinal,
}) {
  if (!isFinal) return { action: 'ignore', reason: 'not_final' };

  const words = normalizedVoiceWords(candidate);
  if (!words.length || words.every((word) => NON_SPEECH_FRAGMENTS.has(word))) {
    return { action: 'ignore', reason: 'non_speech' };
  }
  if (isPathologicalVoiceTranscript(candidate)) {
    return { action: 'ignore', reason: 'recognition_loop' };
  }

  const explicitCommand = DIRECT_COMMANDS.has(words[0]);
  const explicitReplacement = isExplicitReplacementTurn(candidate, assistantText);
  if (!explicitReplacement && isLikelyAssistantEcho(candidate, assistantText)) {
    return { action: 'ignore', reason: 'assistant_echo' };
  }

  // Some Web Speech implementations report confidence as zero when the
  // value is unavailable. Treat that as unknown, not as strong evidence.
  const numericConfidence = Number(confidence);
  const confidenceKnown = Number.isFinite(numericConfidence) && numericConfidence > 0;
  // A finalized lexical correction or direct question is stronger evidence
  // of a deliberate floor claim than Web Speech's inconsistently calibrated
  // confidence value. Only an extremely uncertain result may override that
  // evidence; echo and recognition-loop checks have already failed closed.
  const phrase = words.join(' ');
  const hasCue = CONVERSATIONAL_CUES.some((cue) => phrase.includes(cue));
  const correctionTurn = (hasCue && !phrase.startsWith('what about ')) || (
    words.length >= 2 && ['actually', 'but', 'no', 'wait'].includes(words[0])
  );
  const startsQuestion = words.length >= 2 &&
    ['how', 'what', 'when', 'where', 'which', 'who', 'why'].includes(words[0]) &&
    !phrase.startsWith('what about ');
  const strongLexicalTurn = explicitReplacement || correctionTurn || startsQuestion;
  if (
    !explicitCommand && confidenceKnown &&
    numericConfidence < (strongLexicalTurn ? 0.25 : 0.62)
  ) {
    return { action: 'ignore', reason: 'low_confidence' };
  }

  if (explicitCommand) return { action: 'interrupt', reason: 'direct_command' };

  // Deliberate one-word turn cues take the floor immediately. Arbitrary
  // isolated words remain resumable background fragments because browser
  // recognition does not provide trustworthy speaker identity.
  const singleWordTurn = words.length === 1 && SINGLE_WORD_TURN_CUES.has(words[0]);
  // A short background phrase must not seize the floor merely because a
  // recognizer assigned high confidence. Require either clear turn-taking
  // language or a longer utterance before treating uncued speech as addressed
  // to Sonny. This is intentionally conservative: browser recognition does
  // not provide trustworthy speaker identity.
  const confidentSpeech = !confidenceKnown || numericConfidence >= 0.78;
  const naturalTurn = confidentSpeech && (
    (words.length >= 2 && SHORT_REQUEST_OPENERS.has(words[0])) ||
    (words.length >= 3 && TURN_OPENERS.has(words[0]))
  );

  if (strongLexicalTurn || (confidentSpeech && (singleWordTurn || naturalTurn))) {
    return { action: 'interrupt', reason: 'conversational_turn' };
  }
  return { action: 'ignore', reason: 'background_fragment' };
}

// Ordinary LISTENING is less restrictive than barge-in: a quiet or single-word
// turn must remain possible.  It still rejects artifacts that are provably not
// useful input.  A confidence value of zero is treated as unavailable because
// Web Speech uses zero when the browser does not expose confidence.
export function classifyOrdinaryVoiceTranscript({ candidate, confidence }) {
  const words = normalizedVoiceWords(candidate);
  if (!words.length || words.every((word) => NON_SPEECH_FRAGMENTS.has(word))) {
    return { action: 'ignore', reason: 'non_speech' };
  }
  if (isPathologicalVoiceTranscript(candidate)) {
    return { action: 'ignore', reason: 'recognition_loop' };
  }
  const normalizedCandidate = String(candidate || '').normalize('NFKC').trim();
  if (/^[\d\s.,%+\-:/]+$/u.test(normalizedCandidate) && words.length <= 3) {
    // In an always-listening session, an isolated number is much more likely
    // to be television/notification leakage than a complete addressed turn.
    // People can still ask a numeric question as a normal phrase.
    return { action: 'ignore', reason: 'unaddressed_numeric_fragment' };
  }
  const numericConfidence = Number(confidence);
  const confidenceKnown = Number.isFinite(numericConfidence) && numericConfidence > 0;
  if (confidenceKnown && numericConfidence < 0.25) {
    return { action: 'ignore', reason: 'extremely_low_confidence' };
  }
  return { action: 'accept', reason: confidenceKnown ? 'recognized_speech' : 'confidence_unavailable' };
}

export function shouldPauseForPotentialInterruption({
  candidate,
  assistantText,
  confidence: _confidence,
}) {
  const words = normalizedVoiceWords(candidate);
  if (!words.length || words.every((word) => NON_SPEECH_FRAGMENTS.has(word))) return false;
  if (isPathologicalVoiceTranscript(candidate)) return false;
  if (!isExplicitReplacementTurn(candidate, assistantText) && isLikelyAssistantEcho(candidate, assistantText)) return false;

  // Interim confidence is inconsistent across browsers and often starts low
  // for the first syllable. Yield immediately to any recognized content;
  // final classification decides whether to accept the turn or resume.
  return true;
}

// Playback ownership and caption ownership are separate. Chrome may merge
// Sonny's output into an evolving interim result even after playback has
// yielded. That evidence can keep the bounded interruption check open, but it
// must never be presented as if the user said it.
export function shouldPresentInterruptionCandidate({
  candidate,
  assistantText,
}) {
  if (!normalizedVoiceWords(candidate).length) return false;
  if (isExplicitReplacementTurn(candidate, assistantText)) return true;
  return !isLikelyAssistantEcho(candidate, assistantText);
}

// Every runtime yields playback as soon as its speech detector fires. Final
// transcript, echo, and confidence classification decides whether Sonny keeps
// listening or resumes the exact saved playback offset. A visible tap target
// is an accessibility fallback, never the primary interruption mechanism.
export function shouldYieldPlaybackOnSpeechStart() {
  return true;
}

// A recognizer may emit incidental interim text while a model request is in
// flight. Never cancel that request until a finalized candidate passes the
// same conservative intent, confidence, and echo boundaries used for barge-in.
export function classifyThinkingTurnReplacement({
  candidate,
  assistantText = '',
  confidence,
  isFinal,
}) {
  const decision = classifyVoiceInterruption({
    candidate,
    assistantText,
    confidence,
    isFinal,
  });
  return decision.action === 'interrupt'
    ? { action: 'replace', reason: decision.reason }
    : { action: 'hold', reason: decision.reason };
}

// Represent an observation without turning uncertain browser audio into a
// speaker identity or a conversation turn. The payload is intentionally
// content-free and safe to keep only for the active voice session.
export function classifyEphemeralVoiceObservation({
  candidate,
  assistantText,
  confidence,
  isFinal = true,
  overlapping = false,
} = {}) {
  const decision = classifyVoiceInterruption({
    candidate,
    assistantText,
    confidence,
    isFinal,
  });
  const words = normalizedVoiceWords(candidate);

  if (!words.length || decision.reason === 'non_speech') {
    return Object.freeze({
      kind: 'environmental_noise',
      confidence: 'uncertain',
      turnAction: 'continue',
      retain: false,
    });
  }
  if (decision.reason === 'assistant_echo') {
    return Object.freeze({
      kind: 'assistant_echo',
      confidence: 'probable',
      turnAction: 'continue',
      retain: false,
    });
  }
  if (overlapping) {
    return Object.freeze({
      kind: 'possible_overlapping_speech',
      confidence: 'uncertain',
      turnAction: decision.action === 'interrupt' ? 'validate' : 'continue',
      retain: false,
    });
  }
  if (decision.action === 'interrupt') {
    return Object.freeze({
      kind: 'probable_directed_speech',
      confidence: 'probable',
      turnAction: 'interrupt',
      retain: false,
    });
  }
  return Object.freeze({
    kind: 'possible_background_speech',
    confidence: decision.reason === 'low_confidence' ? 'uncertain' : 'possible',
    turnAction: 'continue',
    retain: false,
  });
}

export function createEphemeralVoiceObservationQueue({
  now = () => Date.now(),
  ttlMs = 5000,
  maxEntries = 8,
} = {}) {
  const entries = [];
  const safeTtl = Math.max(250, Number(ttlMs) || 5000);
  const safeMax = Math.max(1, Math.min(32, Number(maxEntries) || 8));

  const prune = () => {
    const cutoff = Number(now()) - safeTtl;
    while (entries.length && entries[0].observedAt < cutoff) entries.shift();
  };

  return Object.freeze({
    observe(observation) {
      prune();
      if (!observation || observation.retain !== true) return false;
      entries.push(Object.freeze({
        kind: String(observation.kind || 'possible_background_speech'),
        confidence: String(observation.confidence || 'uncertain'),
        observedAt: Number(now()),
      }));
      while (entries.length > safeMax) entries.shift();
      return true;
    },
    snapshot() {
      prune();
      return entries.map(({ kind, confidence, observedAt }) => ({
        kind,
        confidence,
        ageMs: Math.max(0, Number(now()) - observedAt),
      }));
    },
    clear() {
      entries.length = 0;
    },
  });
}

export function observeEphemeralVoiceCandidate({
  queue,
  surface = 'public',
  emit = (event) => console.info('[VOICE_OBSERVATION]', event),
  ...candidateEvidence
} = {}) {
  const observation = classifyEphemeralVoiceObservation(candidateEvidence);
  // Current classifications intentionally retain nothing. Keeping the queue
  // boundary wired makes that privacy rule enforceable if a separately
  // approved, content-free observation type is introduced later.
  queue?.observe?.(observation);
  emit?.(Object.freeze({
    event: 'voice_observation',
    surface: surface === 'developer' ? 'developer' : 'public',
    kind: observation.kind,
    confidence: observation.confidence,
    turn_action: observation.turnAction,
  }));
  return observation;
}

// Pure state-machine decision used by the live component and its regression
// tests. Interim speech pauses playback immediately. The final result either
// confirms the interruption or resumes the same audio from its saved offset.
export function decideVoiceBargeIn({
  candidate,
  assistantText,
  confidence,
  isFinal,
  playbackPaused = false,
}) {
  if (!isFinal) {
    // Once the browser has yielded playback for this speech cycle, a later
    // interim snapshot must not revoke that yield merely because Chrome has
    // merged more of the assistant's playback into the evolving transcript.
    // Keep the bounded pause alive until a phrase boundary; final
    // classification still decides whether to accept the replacement or
    // resume the exact saved playback position.
    if (playbackPaused) {
      return { action: 'pause', reason: 'speech_cycle_in_progress' };
    }
    return shouldPauseForPotentialInterruption({ candidate, assistantText, confidence })
      ? { action: 'pause', reason: 'potential_user_speech' }
      : { action: 'ignore', reason: 'incidental_audio' };
  }

  const decision = classifyVoiceInterruption({
    candidate,
    assistantText,
    confidence,
    isFinal: true,
  });
  if (decision.action === 'interrupt') return decision;
  // A transcript result cannot prove that the speaker has finished. Keep the
  // active playback paused; the component resumes only after speechend (or
  // the bounded silence detector used by the recorded Firefox fallback).
  if (playbackPaused) return { action: 'hold', reason: decision.reason };
  return decision;
}
